import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, PlayCircle, FileText, Hammer, Factory } from "lucide-react";

export default function Resources() {
  useEffect(() => {
    document.title = "Resources — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "Building materials resources: industry insights, how-to guides, product education, and contractor-facing content from LumberLoop USA.");
    window.scrollTo(0, 0);
  }, []);

  const cards = [
    {
      icon: BookOpen,
      title: "Building Insights Blog",
      body: "In-depth articles on building trends, product spotlights, contractor tips, and market analysis. Updated weekly.",
      to: "/insights",
      cta: "Read Insights",
    },
    {
      icon: PlayCircle,
      title: "How It Works",
      body: "A 5-step walkthrough of how LumberLoop USA captures, routes, and measures contractor demand across your dealer network.",
      to: "/how-it-works",
      cta: "See the Flow",
    },
    {
      icon: FileText,
      title: "Platform Overview",
      body: "The full LumberLoop USA platform architecture — capture, route, measure. Built for the way building materials actually move.",
      to: "/platform",
      cta: "Explore Platform",
    },
    {
      icon: Factory,
      title: "For Manufacturers",
      body: "Why building product manufacturers are moving from pure advertising to demand intelligence — and what it means for channel ROI.",
      to: "/manufacturers",
      cta: "Read the Case",
    },
    {
      icon: Hammer,
      title: "For Contractors",
      body: "Free quote-request portal for contractors and builders. Submit once, get quotes from local approved dealers.",
      to: "/request-quote",
      cta: "Request a Quote",
    },
  ];

  return (
    <div data-testid="resources-page">
      <section className="relative bg-[#0F2540] text-white border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-15" aria-hidden="true"/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">Resources</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-3 leading-[0.95]">
            Insights for the <span className="text-[#C62828]">building materials channel.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-3xl">
            Everything we've learned about connecting manufacturers, dealers, and contractors — in one place.
          </p>
        </div>
        <div className="h-2 stripes-flag" aria-hidden="true"/>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map(c => (
            <Link
              key={c.title}
              to={c.to}
              className="brutal-card bg-white p-6 hover:translate-y-[-2px] transition-transform block"
              data-testid={`resource-card-${c.title.replace(/\s+/g,'-').toLowerCase()}`}
            >
              <div className="w-11 h-11 bg-[#C62828] text-white flex items-center justify-center"><c.icon size={20}/></div>
              <h2 className="font-heading text-xl font-bold uppercase mt-4 text-[#0F2540]">{c.title}</h2>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{c.body}</p>
              <div className="mt-5 text-[#C62828] font-bold text-sm inline-flex items-center gap-1">{c.cta} <ArrowRight size={14}/></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#0F2540] text-white py-16 border-t-2 border-[#1A365D] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl font-black uppercase">Want it in a demo?</h2>
          <p className="mt-3 text-slate-300">30 minutes. Your product lines. Your dealer footprint.</p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2 mt-6">Request a Demo <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
}
